import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { InsuranceType } from 'src/app/_enums/insurance-type.enum';
import {
  getLegalProtectionBaseInsuranceLabel,
  LegalProtectionBaseInsuranceEnum,
} from 'src/app/_enums/legal-protection.enums';
import {
  getResidentStatusLabel,
  ResidentStatusEnum,
} from 'src/app/_enums/resident-status.enum';
import { getSexEnumLabel, SexEnum } from 'src/app/_enums/sex.enum';
import { COUNTRIES, LanguageCode } from 'src/app/_helpers/nationalities';
import {
  formatDateForInput,
  getFormArray,
  getGroup,
} from 'src/app/_helpers/utils';
import { LegalProtectionMetadata } from 'src/app/_types/lead.types';

import { LeadFormBase } from '../lead-form-base';

type BuildingFormGroup = {
  appartmentCount: FormControl<number | null>;
  address: FormControl<string | null>;
  postalCode: FormControl<string | null>;
};

type CoInsuredFormGroup = {
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  birthDate: FormControl<string | null>;
  gender: FormControl<SexEnum | null>;
  country: FormControl<string | null>;
};

@Component({
  selector: 'app-lead-legal-protection-form',
  templateUrl: './legal-protection-form.component.html',
  styleUrls: ['./legal-protection-form.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
})
export class LegalProtectionFormComponent
  extends LeadFormBase
  implements OnDestroy, OnInit
{
  @Input()
  formGroup!: FormGroup;

  adultsCountSubscription!: Subscription;

  childrenCountSubscription!: Subscription;

  coversSubscription!: Subscription;

  private _buildingNumberValue: number = 0;

  get buildingNumberValue(): number {
    return this._buildingNumberValue;
  }

  set buildingNumberValue(value: number) {
    if (value >= 0 && value < 6) {
      this._buildingNumberValue = value;
      this.onChangeBuildingNbr();
    }
  }

  private _coInsuredCount: number = 0;

  get coInsuredCount(): number {
    return this._coInsuredCount;
  }

  set coInsuredCount(value: number) {
    this._coInsuredCount = value;
    this.handleCoInsuredCountChange();
  }

  private _startDate!: string;

  get startDate(): string {
    return this._startDate;
  }

  set startDate(value: string) {
    this._startDate = value;
    if (value)
      this.metadataFormGroup.controls['startDate'].setValue(
        new Date(value).toLocaleDateString('fr-FR')
      );
  }

  constructor(
    private fb: FormBuilder,
    private cookieService: CookieService
  ) {
    super();
  }

  metadataFormGroup: FormGroup = this.fb.group({
    type: this.fb.control(InsuranceType.LEGAL_PROTECTION),
    baseInsurance: this.fb.control(null),
    coInsured: this.fb.array<FormGroup<CoInsuredFormGroup>>([]),
    isFamilyInsurance: this.fb.control(null),
    birthDate: this.fb.control(null),
    startDate: this.fb.control(null),
    buildings: this.fb.array<FormGroup<BuildingFormGroup>>([]),
    alreadyClient: this.fb.control(null),
    risk: this.fb.control(null),
    company: this.fb.control(null),
    resiliation: this.fb.control(null),
    userInLitigation: this.fb.control(null),
    adultsCount: this.fb.control(null),
    childrenCount: this.fb.control(null),
    residentStatus: this.fb.control(null),
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.formGroup.removeControl('metadata');
    if (this.metadata) {
      const legalProtectionMetadata = this.metadata as LegalProtectionMetadata;
      this.metadataFormGroup.patchValue(legalProtectionMetadata);
      this.startDate = formatDateForInput(legalProtectionMetadata.startDate);
      this.coInsuredCount = legalProtectionMetadata.coInsured?.length ?? 0;
      this.metadataFormGroup.controls['coInsured'].patchValue(
        legalProtectionMetadata.coInsured
      );
      this.buildingNumberValue = legalProtectionMetadata.buildings?.length ?? 0;
      if (this.buildingNumberValue > 0) {
        this.metadataFormGroup.controls['buildings'].patchValue(
          legalProtectionMetadata.buildings
        );
      }
    }
    this.formGroup.addControl('metadata', this.metadataFormGroup);
    this.adultsCountSubscription = this.metadataFormGroup.controls[
      'adultsCount'
    ].valueChanges.subscribe((value) => {
      this.metadataFormGroup.controls['isFamilyInsurance'].setValue(
        value + (this.metadataFormGroup.controls['childrenCount'].value ?? 0) >
          1
      );
    });
    this.childrenCountSubscription = this.metadataFormGroup.controls[
      'childrenCount'
    ].valueChanges.subscribe((value) => {
      this.metadataFormGroup.controls['isFamilyInsurance'].setValue(
        value + (this.metadataFormGroup.controls['adultsCount'].value ?? 0) > 1
      );
    });

    this.coversSubscription = this.metadataFormGroup.controls[
      'baseInsurance'
    ].valueChanges.subscribe((value) => {
      if (!this.showBuildingsForm()) {
        this.metadataFormGroup.controls['buildings'].reset();
      }
    });
  }

  ngOnDestroy(): void {
    this.childrenCountSubscription.unsubscribe();
    this.adultsCountSubscription.unsubscribe();
    this.coversSubscription.unsubscribe();
  }

  residentStatusValues = Object.values(ResidentStatusEnum);

  getResidentStatusLabel = getResidentStatusLabel;

  LegalProtectionBaseInsuranceEnum = LegalProtectionBaseInsuranceEnum;

  coverValues = Object.values(LegalProtectionBaseInsuranceEnum);

  getCoverLabel = getLegalProtectionBaseInsuranceLabel;

  totalAppartmentsNbr: number = 0;

  showBuildingsForm(): boolean {
    const { value } = this.metadataFormGroup.controls['baseInsurance'];
    return (
      value &&
      (value === LegalProtectionBaseInsuranceEnum.PRIVATE ||
        value === LegalProtectionBaseInsuranceEnum.PRIVATE_AND_CIRCULATION)
    );
  }

  get buildings(): FormArray<FormGroup<BuildingFormGroup>> {
    return this.metadataFormGroup.controls['buildings'] as FormArray<
      FormGroup<BuildingFormGroup>
    >;
  }

  getBuilding(index: number) {
    return this.buildings.at(index);
  }

  onChangeBuildingNbr() {
    const buildingsFormArray = this.metadataFormGroup.controls[
      'buildings'
    ] as FormArray;
    if (this.buildingNumberValue > buildingsFormArray.length) {
      for (
        let i = 0;
        i < this.buildingNumberValue - buildingsFormArray.length;
        i++
      ) {
        buildingsFormArray.push(
          this.fb.group({
            appartmentCount: new FormControl<number | null>(null),
            address: new FormControl<number | null>(null),
            postalCode: new FormControl<number | null>(null),
          })
        );
      }
    } else {
      for (
        let i = 0;
        i < buildingsFormArray.length - this.buildingNumberValue;
        i++
      ) {
        buildingsFormArray.removeAt(buildingsFormArray.length - 1);
      }
    }
  }

  onChangeAppartmentCount() {
    this.totalAppartmentsNbr = this.buildings.controls.reduce(
      (appartNbr, control) =>
        appartNbr + control.get('appartmentCount')?.value!,
      0
    );
  }

  handleCoInsuredCountChange() {
    const coInsuredFormArray = this.metadataFormGroup.controls[
      'coInsured'
    ] as FormArray;
    const coInsuredFormArrayCount = coInsuredFormArray.length;
    if (this.coInsuredCount > coInsuredFormArrayCount) {
      for (let i = 0; i < this.coInsuredCount - coInsuredFormArrayCount; i++) {
        coInsuredFormArray.push(
          this.fb.group({
            firstName: new FormControl<string | null>(null),
            lastName: new FormControl<string | null>(null),
            birthDate: new FormControl<string | null>(null),
            gender: new FormControl<string | null>(null),
            country: new FormControl<string | null>(null),
          })
        );
      }
    } else {
      for (
        let i = 0;
        i < coInsuredFormArray.length - this.coInsuredCount;
        i++
      ) {
        coInsuredFormArray.removeAt(coInsuredFormArray.length - 1);
      }
    }
  }

  getFormArray = getFormArray;

  getFormGroup = getGroup;

  sexOptions = [SexEnum.MALE, SexEnum.FEMALE];

  getSexEnumLabel = getSexEnumLabel;

  countries = COUNTRIES;

  countriesISO = Object.keys(COUNTRIES);

  languageCode =
    (this.cookieService.get('selectedCountryCode') as LanguageCode) ||
    ('fr' as LanguageCode);
}
